import React, { useEffect, useState } from "react";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import { MaterialReactTable } from "material-react-table";
import { Edit, Delete } from "@mui/icons-material"; // Importing Material UI icons
import { FaMailBulk, FaPlane } from "react-icons/fa"; // Importing the plane icon for airlines
import InputField from "components/fields/InputField";
import userResources from "api/userResources";
import { toast } from "react-toastify";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import SubmitButton from "components/SubmitButton/SubmitButton";
import { selectUserInfo } from "../../../redux/userSlice";
import { useSelector } from "react-redux";

export default function Airport() {
  const userInfo = useSelector(selectUserInfo);
  const [airlinesData, setAirlinesData] = useState([]);
  const [editingAirline, setEditingAirline] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    iata: "",
    country: "",
    price: "",
    icao: "",
  });
  const [errors, setErrors] = useState({});

  const getairport = async (pageIndex, pageSize, globalFilter) => {
    setIsLoading(true); // Set loading state to true when fetching starts
    const params = {
      page: pageIndex || 0,
      limit: pageSize || 10,
      iata: globalFilter,
    };
    try {
      const response = await userResources.airportDataList(params);
      setAirlinesData(response.data.data);
      setTotalRows(response.data.paginate_data.total); // Set total rows for pagination
    } catch (error) {
      console.error("Error fetching airport data:", error);
    } finally {
      setIsLoading(false); // Set loading state to false when fetching ends
    }
  };

  const validateField = (name, value) => {
    // Field validation logic
    let errorMessage = "";
    if (name === "name" && !value) {
      errorMessage = "Airport name is required.";
    } else if (name === "icao" && !value) {
      errorMessage = "ICAO code is required.";
    }
    return errorMessage;
  };

  const handleChange = (name, value) => {
    const fieldValue = value.target.value;

    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: fieldValue,
      };
      return updatedData;
    });

    // Validate the field and set error message if needed
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: validateField(name, fieldValue),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields on form submission
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const errorMessage = validateField(key, formData[key]);
      if (errorMessage) {
        newErrors[key] = errorMessage;
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors before submitting.");
      return; // If there are errors, do not proceed
    }

    // Show loading toast
    setDisabled(true);
    const loadingToastId = toast.loading("Submitting...");

    try {
      if (editingAirline) {
        // Update existing airport
        await userResources.addAirpost({
          id: editingAirline.id,
          ...formData,
        });
        setEditingAirline(null);
      } else {
        // Add new airport
        await userResources.addAirpost({
          ...formData,
        });
      }

      // Refresh the airport list
      getairport();

      // Update toast to success
      toast.update(loadingToastId, {
        render: editingAirline
          ? "Airport updated successfully."
          : "Airport added successfully.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // Reset form data after successful submission
      setFormData({
        name: "",
        iata: "",
        country: "",
        price: "",
        icao: "",
      });
    } catch (error) {
      console.error("Error submitting form data:", error);

      // Update toast to show error
      const errorMessage =
        error.response?.data?.message ||
        "Failed to submit form. Please try again.";
      toast.update(loadingToastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });

      // Handle individual field errors if needed
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      // Ensure disabled state is reset regardless of success or error
      setDisabled(false);
    }
  };

  const airlinesColumns = [
    { accessorKey: "id", header: "ID" },
    { accessorKey: "name", header: "Airport" },
    { accessorKey: "icao", header: "ICAO" },
    { accessorKey: "iata", header: "IATA" },
    { accessorKey: "country", header: "Country" },
    { accessorKey: "state", header: "State" },
    { accessorKey: "city", header: "City" },
    {
      accessorKey: "created_at",
      header: "Created at",
      Cell: ({ cell }) => {
        // Extract and format the date
        const date = new Date(cell.getValue()); // Create a Date object
        const formattedDate = date.toISOString().split("T")[0]; // Get only the date part (YYYY-MM-DD)
        return <span>{formattedDate}</span>; // Display the formatted date
      },
    },
    {
      accessorKey: "updated_at",
      header: "Updated at",
      Cell: ({ cell }) => {
        // Extract and format the date
        const date = new Date(cell.getValue()); // Create a Date object
        const formattedDate = date.toISOString().split("T")[0]; // Get only the date part (YYYY-MM-DD)
        return <span>{formattedDate}</span>; // Display the formatted date
      },
    },
    {
      accessorKey: "action",
      header: "Action",
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <Edit
            className="cursor-pointer text-blue-500"
            onClick={() => handleEdit(row.original)}
            aria-label={`Edit ${row.original.name}`}
          />
          <div>
            {userInfo?.data?.role_id ===
            Number(process.env.REACT_APP_ROLE_EMPLOYEE) ? null : (
              <ConfirmModal
                onConfirm={handleDelete}
                title="Delete Airport"
                message="Are you sure you want to delete this airport?"
                employeeId={row.original.id}
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  const handleEdit = (airport) => {
    setFormData({
      name: airport?.name,
      iata: airport?.iata,
      country: airport?.country,
      price: airport?.price,
      icao: airport?.icao,
      state: airport?.state,
      city: airport?.city,
    });
    setEditingAirline(airport);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setEditingAirline(null);
    setFormData({
      name: "",
      iata: "",
      country: "",
      price: "",
      icao: "",
    });
    setErrors({});
  };

  const handleDelete = async (id) => {
    // Show loading toast
    const loadingToastId = toast.loading("Deleting airport...");

    try {
      await userResources.airportDelete(id); // Attempt to delete the airport

      // Update toast to show success
      toast.update(loadingToastId, {
        render: "Airport deleted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });

      // Refresh the airport list
      getairport(pagination.pageIndex, pagination.pageSize, globalFilter);
    } catch (error) {
      console.error("Error deleting airport:", error);

      // Update toast to show error
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete airport. Please try again.";
      toast.update(loadingToastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      // Dismiss the loading toast if not already dismissed
      if (toast.isActive(loadingToastId)) {
        toast.dismiss(loadingToastId);
      }
    }
  };

  const handleGlobalFilterChange = (value) => {
    setGlobalFilter(value); // Update the global filter state with the new value
  };

  // Calculate total pages
  const totalPages = Math.ceil(airlinesData.length / pagination.pageSize);

  // Navigate to first page
  const goToFirstPage = () => {
    setPagination({ ...pagination, pageIndex: 0 });
  };

  // Navigate to last page
  const goToLastPage = () => {
    setPagination({ ...pagination, pageIndex: totalPages - 1 });
  };

  useEffect(() => {
    getairport();
  }, []);

  useEffect(() => {
    getairport(pagination.pageIndex, pagination.pageSize, globalFilter);
  }, [pagination, globalFilter]);

  const handlePaginationChange = (updatedPagination) => {
    setPagination(updatedPagination);
  };

  return (
    <section className="mt-6">
      <ScrollToTopOnRouteChange />
      <div className="grid grid-cols-1 gap-6">
        {/* Add Airport Form */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-4 text-xl font-semibold dark:text-white">
            {editingAirline ? "Edit Airport" : "Add Airport"}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-2  sm:grid-cols-2 md:grid-cols-3">
              <InputField
                label="Airport Name"
                name="name"
                type="text"
                placeholder="Airport Name"
                value={formData.name}
                onChange={(value) => handleChange("name", value)}
                state={errors.name ? "error" : ""}
                errorMessage={errors.name}
              />
              <InputField
                label="ICAO Code"
                name="icao"
                type="text"
                placeholder="ICAO Code"
                value={formData.icao}
                onChange={(value) => handleChange("icao", value)}
                state={errors.icao ? "error" : ""}
                errorMessage={errors.icao}
              />
              <InputField
                label="IATA"
                name="iata"
                type="text"
                placeholder="IATA"
                value={formData.iata}
                onChange={(value) => handleChange("iata", value)}
              />
              <InputField
                label="Country"
                name="country"
                type="text"
                placeholder="Country"
                value={formData.country}
                onChange={(value) => handleChange("country", value)}
              />
              <InputField
                label="State"
                name="state"
                type="text"
                placeholder="State"
                value={formData.state}
                onChange={(value) => handleChange("state", value)}
              />
              <InputField
                label="City"
                name="city"
                type="text"
                placeholder="City"
                value={formData.city}
                onChange={(value) => handleChange("city", value)}
              />
            </div>
            <div className="mt-6 flex space-x-2">
              {editingAirline && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full rounded-lg border border-red-500 p-2 text-red-500 hover:bg-red-500 hover:text-white focus:outline-none"
                >
                  Cancel
                </button>
              )}
              <SubmitButton
                editing={editingAirline}
                disabled={disabled}
                add="Add Airport"
                edit="Update Airport"
              />
            </div>
          </form>
        </div>

        {/* Airlines Table */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-gray-900">
          <h3 className="mb-4 text-xl font-semibold dark:text-white">
            Airlines List
          </h3>
          <MaterialReactTable
            columns={airlinesColumns}
            data={airlinesData}
            state={{ pagination, isLoading }} // Use both states here
            // state={{ pagination, isLoading, showProgressBars: isLoading }} // Use both states here
            enablePagination
            onPaginationChange={handlePaginationChange}
            manualPagination
            rowCount={totalRows}
            pageCount={Math.ceil(totalRows / pagination.pageSize)}
            isLoading={isLoading}
            enableColumnFilters={false}
            enableSorting={false}
            enableColumnOrdering={false}
            muiTablePaginationProps={{
              showFirstButton: true,
              showLastButton: true,
            }}
            enableGlobalFilter // Keep this true to allow global filtering
            onGlobalFilterChange={handleGlobalFilterChange} // Update the state on filter change
            globalFilter={globalFilter} // Pass the state variable to the table
          />
        </div>
      </div>
    </section>
  );
}
